import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { X, Send, MessageSquare, Loader2, Hash, User as UserIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const buildWsUrl = (threadId) => {
  const base = process.env.REACT_APP_BACKEND_URL || '';
  let url;
  try { url = new URL(base); } catch { url = new URL(window.location.origin); }
  const proto = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${url.host}/api/direct-chat/threads/${threadId}/ws`;
};

const ROLE_BADGE = {
  super_admin: { label: 'Admin', bg: '#FF9500' },
  company_admin: { label: 'Admin', bg: '#002FA7' },
  dispatcher: { label: 'Dispatcher', bg: '#7C3AED' },
  driver: { label: 'Driver', bg: '#34C759' },
};

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const fmtDateHeader = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'Today';
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * DirectChatDrawer
 * Renders a side panel for a DM or department channel thread.
 * Props: { thread: {id, kind, department_label, other_user}, open, onClose, onClosedAfterMessage }
 */
const DirectChatDrawer = ({ thread, open, onClose, onClosedAfterMessage }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const listRef = useRef(null);
  const threadId = thread?.id;

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  }, []);

  useEffect(() => {
    if (!open || !threadId) return;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const { data } = await axios.get(`${API}/direct-chat/threads/${threadId}/messages`, { withCredentials: true });
        setMessages(data);
        scrollToBottom();
        // Mark read on open
        try { await axios.post(`${API}/direct-chat/threads/${threadId}/read`, {}, { withCredentials: true }); } catch { /* */ }
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load messages');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, threadId, scrollToBottom]);

  useEffect(() => {
    if (!open || !threadId) return undefined;
    let ws;
    try { ws = new WebSocket(buildWsUrl(threadId)); } catch { return undefined; }
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);
    ws.onmessage = (evt) => {
      try {
        const payload = JSON.parse(evt.data);
        if (payload.type === 'message' && payload.data) {
          setMessages((prev) => prev.some((m) => m.id === payload.data.id) ? prev : [...prev, payload.data]);
          scrollToBottom();
          // If the incoming message wasn't ours, mark as read so unread count clears
          if (payload.data.sender_id !== (user?.id || user?._id)) {
            axios.post(`${API}/direct-chat/threads/${threadId}/read`, {}, { withCredentials: true }).catch(() => {});
          }
        }
      } catch { /* ignore */ }
    };
    return () => {
      try { ws.close(); } catch { /* */ }
      wsRef.current = null;
    };
  }, [open, threadId, scrollToBottom, user]);

  const send = async (e) => {
    e?.preventDefault?.();
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    setError('');
    try {
      await axios.post(`${API}/direct-chat/threads/${threadId}/messages`, { body }, { withCredentials: true });
      setDraft('');
      if (onClosedAfterMessage) onClosedAfterMessage();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!open || !thread) return null;

  const isChannel = thread.kind === 'channel';
  const title = isChannel
    ? `#${thread.department_label || thread.department || 'channel'}`
    : (thread.other_user?.name || 'Direct message');
  const subtitle = isChannel
    ? `${thread.department_label || ''} department channel`
    : (thread.other_user?.role ? ROLE_BADGE[thread.other_user.role]?.label || thread.other_user.role : '');

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="direct-chat-drawer">
      <div className="flex-1 bg-slate-900/40" onClick={onClose} data-testid="direct-chat-backdrop" />
      <aside className="w-full sm:w-[440px] bg-white shadow-2xl flex flex-col">
        <header className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-[#002FA7]/10 flex items-center justify-center">
              {isChannel ? <Hash className="w-4 h-4 text-[#002FA7]" strokeWidth={2.25} /> : <UserIcon className="w-4 h-4 text-[#002FA7]" strokeWidth={2.25} />}
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-slate-900 truncate" style={{ fontFamily: 'Work Sans, sans-serif' }} data-testid="direct-chat-title">
                {title}
              </h2>
              <p className="text-xs text-slate-500 truncate flex items-center gap-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${connected ? 'bg-[#34C759]' : 'bg-slate-300'}`} />
                {subtitle ? `${subtitle} · ` : ''}{connected ? 'Live' : 'Connecting…'}
              </p>
            </div>
          </div>
          <button
            data-testid="direct-chat-close"
            onClick={onClose}
            className="p-2 rounded-md hover:bg-slate-100 text-slate-500"
            aria-label="Close chat"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-4 bg-[#F8FAFC]" data-testid="direct-chat-list">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <Loader2 className="w-4 h-4 animate-spin" /> Loading messages…
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-sm text-slate-500 py-10" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-2" strokeWidth={1.5} />
              No messages yet. Say hello to get the conversation started.
            </div>
          ) : (
            messages.reduce((acc, m, i) => {
              const prev = messages[i - 1];
              const dateLabel = fmtDateHeader(m.created_at);
              const prevDateLabel = prev ? fmtDateHeader(prev.created_at) : null;
              if (!prev || dateLabel !== prevDateLabel) {
                acc.push(
                  <div key={`d-${m.id}`} className="my-3 flex items-center gap-2">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{dateLabel}</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                );
              }
              const isMe = m.sender_id === (user?.id || user?._id);
              const role = ROLE_BADGE[m.sender_role] || { label: 'User', bg: '#94A3B8' };
              acc.push(
                <div key={m.id} data-testid={`direct-msg-${m.id}`} className={`mb-3 flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[78%] ${isMe ? 'items-end' : 'items-start'} flex flex-col`}>
                    {!isMe && (
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{m.sender_name}</span>
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wide text-white" style={{ backgroundColor: role.bg, fontFamily: 'IBM Plex Sans, sans-serif' }}>
                          {role.label.toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div
                      className={`rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words ${
                        isMe ? 'bg-[#002FA7] text-white rounded-br-md' : 'bg-white text-slate-900 border border-slate-200 rounded-bl-md'
                      }`}
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      {m.body}
                    </div>
                    <span className="text-[10px] text-slate-400 mt-1 px-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{fmtTime(m.created_at)}</span>
                  </div>
                </div>
              );
              return acc;
            }, [])
          )}
        </div>

        {error && (
          <div data-testid="direct-chat-error" className="px-5 py-2 text-xs text-red-700 bg-red-50 border-t border-red-100" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {error}
          </div>
        )}

        <form onSubmit={send} className="px-4 py-3 border-t border-slate-200 bg-white" data-testid="direct-chat-form">
          <div className="flex items-end gap-2">
            <textarea
              data-testid="direct-chat-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={isChannel ? `Message #${thread.department_label || thread.department}…` : 'Type a message…'}
              rows={2}
              className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent focus:outline-none resize-none"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              disabled={sending}
            />
            <button
              type="submit"
              disabled={sending || !draft.trim()}
              data-testid="direct-chat-send"
              className="bg-[#002FA7] text-white p-2.5 rounded-md hover:bg-[#0034BD] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={2.25} />}
            </button>
          </div>
          <p className="text-[10px] text-slate-400 mt-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Enter to send · Shift+Enter for a new line
          </p>
        </form>
      </aside>
    </div>
  );
};

export default DirectChatDrawer;
